// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  ConciliacaoBancaria.tsx
//  Marca lançamentos de caixa como conciliados (conferidos com extrato bancário).
// ════════════════════════════════════════════════════════════════════════════

import { useMemo, useState } from 'react';
import {
  CheckCircle2, Circle, RefreshCw, GitMerge,
  ChevronLeft, ChevronRight, ListChecks,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  useCaixaExtrato,
  useCaixaPeriodo,
  useCaixaConciliar,
  useCaixaConciliarLote,
} from '@/features/caixa/hooks/useCaixa';
import { CaixaSelect, useNomeCaixa } from '@/components/CaixaSelect';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';


const hoje      = new Date().toISOString().slice(0, 10);
const primeiroDia = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  .toISOString().slice(0, 10);

const PAGE_SIZES = [10, 20, 50] as const;
const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmt = (d: string) => new Date(d).toLocaleDateString('pt-BR');

export default function ConciliacaoBancaria() {
  // Mesmo seletor da página Caixa: 'todos' (default) ou um caixa com movimento no período.
  const [caixaSel, setCaixaSel]   = useState<number | undefined>();
  const [dtInicio, setDtInicio]   = useState(primeiroDia);
  const [dtFim, setDtFim]         = useState(hoje);
  const [pag,           setPag]           = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [apenasAbertos, setApenasAbertos] = useState(false);

  const caixaId = caixaSel ?? 0;
  const nomeCaixa = useNomeCaixa();
  const todos   = caixaId === 0;

  const { data: extrato, isLoading: loadingExtrato, refetch: refetchExtrato } =
    useCaixaExtrato(caixaId, dtInicio, dtFim);
  const { data: periodo = [], isLoading: loadingPeriodo, refetch: refetchPeriodo } =
    useCaixaPeriodo(dtInicio, dtFim);
  const conciliarMut     = useCaixaConciliar();
  const conciliarLoteMut = useCaixaConciliarLote();


  const lancamentos = todos ? periodo : (extrato?.lancamentos ?? []);
  const isLoading   = todos ? loadingPeriodo : loadingExtrato;
  const refetch     = () => { refetchPeriodo(); if (!todos) refetchExtrato(); };
  const colunas     = todos ? 8 : 7;
  const filtered    = apenasAbertos ? lancamentos.filter((l) => !l.isConciliado) : lancamentos;
  const total    = filtered.length;
  const totalPag = Math.max(1, Math.ceil(total / tamanhoPagina));
  const slice    = filtered.slice((pag - 1) * tamanhoPagina, pag * tamanhoPagina);
  const firstItem = total === 0 ? 0 : (pag - 1) * tamanhoPagina + 1;
  const lastItem  = Math.min(pag * tamanhoPagina, total);
  const handlePageSizeChange = (value: string) => { setTamanhoPagina(Number(value)); setPag(1); };

  // Seleção ──────────────────────────────────────────────────────────────
  const pendentesIds = slice.filter((l) => !l.isConciliado).map((l) => l.id);
  const allSelected  = pendentesIds.length > 0 && pendentesIds.every((id) => selectedIds.includes(id));
  const someSelected = pendentesIds.some((id) => selectedIds.includes(id));

  function toggleAll() { setSelectedIds(allSelected ? [] : pendentesIds); }
  function toggleOne(id: number) {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  function handleConciliarLote() {
    if (selectedIds.length === 0) return;
    conciliarLoteMut.mutate(
      { ids: selectedIds, conciliar: true },
      { onSuccess: () => setSelectedIds([]) },
    );
  }

  // KPIs ─────────────────────────────────────────────────────────────────
  const totalLanc       = lancamentos.length;
  const conciliados     = lancamentos.filter((l) => l.isConciliado).length;
  const pendentes       = totalLanc - conciliados;
  const valorConciliado = lancamentos
    .filter((l) => l.isConciliado && l.debitoCredito === 'C')
    .reduce((s, l) => s + l.valor, 0)
    - lancamentos
    .filter((l) => l.isConciliado && l.debitoCredito === 'D')
    .reduce((s, l) => s + l.valor, 0);

  return (
    <div className="p-6 space-y-6">
      {/* ── Cabeçalho ── */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <GitMerge className="h-7 w-7" /> Conciliação Bancária
          </h1>
          <p className="text-muted-foreground mt-1">
            Confirme os lançamentos que batem com o extrato bancário
          </p>
        </div>
        <div className="flex gap-2">
          {selectedIds.length > 0 && (
            <Button
              variant="default" className="gap-2"
              onClick={handleConciliarLote}
              disabled={conciliarLoteMut.isPending}
            >
              <ListChecks className="h-4 w-4" />
              Conciliar Selecionados ({selectedIds.length})
            </Button>
          )}
          <Button variant="outline" size="icon" onClick={() => refetch()} title="Atualizar">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* ── Filtros ── */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-1">
              <Label>Caixa</Label>
              <CaixaSelect value={caixaSel} onChange={(v) => { setCaixaSel(v); setPag(1); setSelectedIds([]); }} rotuloTodos="Todos os caixas" className="w-56" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="dtIni">De</Label>
              <Input id="dtIni" type="date" value={dtInicio}
                onChange={(e) => { setDtInicio(e.target.value); setPag(1); }} className="w-36" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="dtFim">Até</Label>
              <Input id="dtFim" type="date" value={dtFim}
                onChange={(e) => { setDtFim(e.target.value); setPag(1); }} className="w-36" />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="apenasAbertos"
                checked={apenasAbertos}
                onCheckedChange={(v) => { setApenasAbertos(!!v); setPag(1); setSelectedIds([]); }}
              />
              <Label htmlFor="apenasAbertos" className="cursor-pointer">Apenas não conciliados</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── KPIs ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs text-muted-foreground">Total Lançamentos</CardTitle>
          </CardHeader>
          <CardContent><p className="text-2xl font-bold">{totalLanc}</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs text-muted-foreground flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3 text-green-600" /> Conciliados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-green-600">{conciliados}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs text-muted-foreground flex items-center gap-1">
              <Circle className="h-3 w-3 text-yellow-500" /> Pendentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-yellow-600">{pendentes}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs text-muted-foreground">Saldo Conciliado</CardTitle>
          </CardHeader>
          <CardContent>
            <p className={`text-2xl font-bold ${valorConciliado < 0 ? 'text-red-600' : ''}`}>
              {brl(valorConciliado)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* ── Tabela ── */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Lançamentos</CardTitle>
            <span className="text-sm text-muted-foreground">{filtered.length} registro(s)</span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleAll}
                      data-state={someSelected && !allSelected ? 'indeterminate' : undefined}
                    />
                  </TableHead>
                  <TableHead>Data</TableHead>
                  {todos && <TableHead>Caixa</TableHead>}
                  <TableHead>Descrição</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: colunas }).map((__, j) => (
                        <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : slice.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={colunas} className="text-center py-8 text-muted-foreground">
                      Nenhum lançamento encontrado
                    </TableCell>
                  </TableRow>
                ) : slice.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell>
                      {!l.isConciliado && (
                        <Checkbox
                          checked={selectedIds.includes(l.id)}
                          onCheckedChange={() => toggleOne(l.id)}
                        />
                      )}
                    </TableCell>
                    <TableCell>{fmt(l.dtMovimento)}</TableCell>
                    {todos && <TableCell className="whitespace-nowrap">{nomeCaixa(l.caixaId)}</TableCell>}
                    <TableCell className="max-w-[200px] truncate" title={l.descricao}>
                      {l.descricao}
                    </TableCell>
                    <TableCell>
                      <Badge className={l.debitoCredito === 'C'
                        ? 'bg-green-500/10 text-green-600'
                        : 'bg-red-500/10 text-red-600'}>
                        {l.debitoCredito === 'C' ? 'Crédito' : 'Débito'}
                      </Badge>
                    </TableCell>
                    <TableCell className={`text-right font-medium ${l.debitoCredito === 'C' ? 'text-green-600' : 'text-red-600'}`}>
                      {l.debitoCredito === 'D' ? '– ' : '+ '}{brl(l.valor)}
                    </TableCell>
                    <TableCell>
                      {l.isConciliado ? (
                        <Badge className="bg-blue-500/10 text-blue-600">
                          <CheckCircle2 className="h-3 w-3 mr-1" /> Conciliado
                        </Badge>
                      ) : (
                        <Badge variant="outline">Pendente</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => conciliarMut.mutate(l.id)}
                        disabled={conciliarMut.isPending}
                        title={l.isConciliado ? 'Desfazer conciliação' : 'Marcar como conciliado'}
                      >
                        {l.isConciliado
                          ? <Circle className="h-4 w-4 text-muted-foreground" />
                          : <CheckCircle2 className="h-4 w-4 text-blue-600" />}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Paginação */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
            <p className="text-sm text-muted-foreground">
              {total === 0 ? 'Nenhum registro'
                : `Mostrando ${firstItem}–${lastItem} de ${total} registro${total !== 1 ? 's' : ''}`}
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">Itens por página:</span>
                <Select value={String(tamanhoPagina)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="w-[80px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPag(1)} disabled={pag === 1}>«</Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPag((p) => p - 1)} disabled={pag === 1}><ChevronLeft className="h-4 w-4" /></Button>
                {Array.from({ length: Math.min(5, totalPag) }, (_, i) => {
                  let page: number;
                  if      (totalPag <= 5)       page = i + 1;
                  else if (pag <= 3)             page = i + 1;
                  else if (pag >= totalPag - 2)  page = totalPag - 4 + i;
                  else                           page = pag - 2 + i;
                  return (
                    <Button key={page} variant={pag === page ? 'default' : 'outline'}
                      size="icon" className="h-8 w-8 text-xs" onClick={() => setPag(page)}>{page}</Button>
                  );
                })}
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPag((p) => p + 1)} disabled={pag >= totalPag}><ChevronRight className="h-4 w-4" /></Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPag(totalPag)} disabled={pag >= totalPag}>»</Button>
              </div>
              <span className="text-sm text-muted-foreground whitespace-nowrap">Página {pag} de {totalPag}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
